import type { Viewport } from "./Viewport";
import { isTextTarget } from "../isTextTarget";
import { wheelZoomFactor } from "./wheelZoom";
import { decidePointerGesture } from "./touchGesture";
import { beginPinch, updatePinch, type PinchState } from "./pinchGesture";
import { hasHover, notePointerKind } from "./pointerPrecision";

export interface PointerSample {
  x: number;
  y: number;
  pressure: number;
  pointerType: "mouse" | "pen" | "touch";
  pointerId: number;
  buttons: number;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}

export interface ToolHandler {
  onDown?: (s: PointerSample, projectPt: { x: number; y: number }) => void;
  onMove?: (s: PointerSample, projectPt: { x: number; y: number }) => void;
  onUp?: (s: PointerSample, projectPt: { x: number; y: number }) => void;
  onHover?: (s: PointerSample, projectPt: { x: number; y: number }) => void;
  onCancel?: () => void;
  /**
   * Whether the gesture in flight should SURVIVE a second finger landing
   * (#40). Default (undefined / false) keeps the old rule: the second contact
   * abandons the gesture and starts a pinch, which is right for a stroke being
   * sketched — you reached for the zoom, and a stray mark is worse than a lost
   * stroke.
   *
   * A tool returning true is saying the opposite trade applies: it is dragging
   * an EXISTING thing to a deliberate place, so the work is worth more than
   * the pinch, and on a tablet the second contact is usually the hand you are
   * resting rather than a zoom you asked for. The extra contact is then
   * ignored for the rest of the gesture.
   *
   * The same answer decides a `pointercancel` from the browser (#1112): a
   * gesture that survives a second contact is COMMITTED at its last position,
   * where the default abandons it.
   */
  gestureSurvivesSecondContact?: () => boolean;
  /** Fired by the app when a global modifier-key state changes
   *  (currently Shift) so a tool with an in-flight preview can
   *  re-render with the new modifier state without waiting for the
   *  next mouse-move event. Tools that don't have modifier-aware
   *  previews can leave this undefined. */
  onModifiersChanged?: (modifiers: {
    shiftKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    altKey: boolean;
  }) => void;
}

interface ActivePointer {
  id: number;
  type: "mouse" | "pen" | "touch";
  x: number;
  y: number;
  startX: number;
  startY: number;
}

/**
 * Single PointerEvent listener that splits pen/touch/mouse and handles
 * pan/zoom gestures while delegating draw events to the active tool.
 *
 * Palm rejection: while any pen pointer is down, all touch pointers are ignored.
 * Two simultaneous touch pointers pan and zoom together (`pinchGesture.ts`).
 *
 * What ONE finger does is `touchGesture.ts`'s call (#40) — it panned
 * unconditionally until then, which is why a finger could never draw. It
 * draws now; two fingers navigate either way, which is what makes that safe.
 */
export class PointerInput {
  private viewport: Viewport;
  private el: HTMLCanvasElement;
  private active = new Map<number, ActivePointer>();
  private penActive = false;
  private toolHandler: ToolHandler | null = null;
  private spaceDown = false;
  private gesture: "none" | "pan" | "pinch" | "draw" = "none";
  private pinch: PinchState | null = null;
  /** A pinch update is already scheduled for the next frame. Two fingers emit
   *  SEPARATE `pointermove` events, so acting on each one in turn measures the
   *  gap while one finger's position is still a frame old — a fast two-finger
   *  slide then reads as a pinch, which is the bug this coalescing fixes.
   *  Waiting for the frame lets both fingers land before anything is applied,
   *  and a finger held perfectly still (which emits no events at all) still
   *  contributes its true position. */
  private pinchQueued = false;
  /** Which contact owns the draw in flight. Moves and releases from any other
   *  pointer are ignored while it lasts — otherwise a resting hand's events
   *  would drive, or end, a drag it never started (#40). */
  private drawPointerId: number | null = null;
  /** The last sample the tool saw for the drag in flight — where a gesture
   *  the browser cancels is committed (#1112). A `pointercancel` carries no
   *  trustworthy position of its own. */
  private lastDrawSample: PointerSample | null = null;
  private panLastX = 0;
  private panLastY = 0;
  /** #40. Defaults to the pre-existing behaviour, so a host that never calls
   *  the setter below behaves exactly as this class did before. */
  private touchDrawEnabled = false;

  constructor(viewport: Viewport, canvas: HTMLCanvasElement) {
    this.viewport = viewport;
    this.el = canvas;

    canvas.addEventListener("pointerdown", this.handleDown);
    canvas.addEventListener("pointermove", this.handleMove);
    canvas.addEventListener("pointerup", this.handleUp);
    canvas.addEventListener("pointercancel", this.handleCancel);
    canvas.addEventListener("pointerleave", this.handleUp);
    canvas.addEventListener("wheel", this.handleWheel, { passive: false });
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());

    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("keyup", this.handleKeyUp);
  }

  /** The `touch-device-support` flag (on by default). Off means every touch
   *  contact pans, as it did before touch drawing. */
  setTouchDrawEnabled(on: boolean) {
    this.touchDrawEnabled = on;
  }

  setTool(handler: ToolHandler | null) {
    if (this.toolHandler && this.gesture === "draw") {
      this.toolHandler.onCancel?.();
    }
    this.toolHandler = handler;
    this.gesture = "none";
  }

  private toSample(e: PointerEvent): PointerSample {
    const rect = this.el.getBoundingClientRect();
    const type = (e.pointerType || "mouse") as "mouse" | "pen" | "touch";
    notePointerKind(type);
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      pressure: e.pressure > 0 ? e.pressure : type === "pen" ? 0.5 : 1,
      pointerType: type,
      pointerId: e.pointerId,
      buttons: e.buttons,
      shiftKey: e.shiftKey,
      altKey: e.altKey,
      metaKey: e.metaKey,
      ctrlKey: e.ctrlKey,
    };
  }

  private project(s: PointerSample) {
    const p = this.viewport.viewToProject({ x: s.x, y: s.y });
    return { x: p.x, y: p.y };
  }

  private isTouchSuppressed(s: PointerSample): boolean {
    return s.pointerType === "touch" && this.penActive;
  }

  private handleDown = (e: PointerEvent) => {
    e.preventDefault();
    const s = this.toSample(e);
    if (this.isTouchSuppressed(s)) return;
    // Pressing on the canvas pulls focus off any input or button so the
    // editor's keyboard shortcuts work again (issue #506). The
    // preventDefault above suppresses the browser's own focus shift, so
    // blur the active element explicitly.
    const focused = document.activeElement;
    if (
      focused instanceof HTMLElement &&
      focused !== document.body &&
      focused !== this.el
    ) {
      focused.blur();
    }
    // setPointerCapture throws (InvalidStateError) when the pointer is no
    // longer active by the time the handler runs — and on synthetic
    // PointerEvents that aren't backed by a live OS pointer. Letting it
    // throw would abort the rest of this handler and silently swallow the
    // draw's onDown. Guard it the same way releasePointerCapture is
    // guarded in handleUp; losing capture only costs us moves once the
    // cursor leaves the canvas, never the gesture itself.
    try {
      this.el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }

    if (s.pointerType === "pen") {
      // Palm rejection, the half that only matters once a finger can draw
      // (#40). A hand lands on the glass BEFORE the pen tip does, so by the
      // time we get here a palm contact has already started a draw — and
      // suppressing its moves from now on would leave that stroke open until
      // an unsuppressed pointerup committed it. Cancel it outright.
      if (!this.penActive && this.gesture === "draw") {
        const palm = [...this.active.values()].some((p) => p.type === "touch");
        if (palm) {
          this.toolHandler?.onCancel?.();
          this.gesture = "none";
        }
      }
      this.penActive = true;
    }

    this.active.set(s.pointerId, {
      id: s.pointerId,
      type: s.pointerType,
      x: s.x,
      y: s.y,
      startX: s.x,
      startY: s.y,
    });

    // Two fingers: pan and zoom together — see `pinchGesture.ts`.
    if (s.pointerType === "touch" && this.active.size === 2) {
      // ...unless a deliberate drag is in flight and its tool asks to keep it.
      // A hand resting on the glass while you drag a corner handle used to
      // abandon the drag and revert the shape, which reads as "it works, then
      // undoes itself on release".
      if (
        this.gesture === "draw" &&
        this.toolHandler?.gestureSurvivesSecondContact?.() === true
      ) {
        return;
      }
      const [a, b] = [...this.active.values()];
      if (this.toolHandler && this.gesture === "draw")
        this.toolHandler.onCancel?.();
      this.gesture = "pinch";
      this.pinch = a && b ? beginPinch(a, b, this.viewport.zoom) : null;
      return;
    }

    // One contact: pan or draw. Middle mouse and space+drag pan; a pen and a
    // finger both draw — see `touchGesture.ts`.
    const decision = decidePointerGesture({
      pointerType: s.pointerType,
      primaryDown: (s.buttons & 1) !== 0,
      mousePanRequested:
        s.pointerType === "mouse" && (e.button === 1 || this.spaceDown),
      touchDrawEnabled: this.touchDrawEnabled,
    });
    if (decision === "pan") {
      this.gesture = "pan";
      this.panLastX = s.x;
      this.panLastY = s.y;
      return;
    }
    if (decision === "draw") {
      this.gesture = "draw";
      this.drawPointerId = s.pointerId;
      this.lastDrawSample = s;
      const pt = this.project(s);
      // A finger has no hover, and neither does a stylus on most tablets. For
      // tools whose press acts on HOVERED state — the mirror axis is the clear
      // case (#40) — that state was never built, so the press found nothing
      // under it and silently did nothing. The press is the first and only
      // chance those tools get to learn where the contact is, so give them the
      // hover a cursor would have delivered, immediately before it. A mouse is
      // excluded because it has genuinely hovered already; re-firing would be
      // redundant work on every click.
      if (!hasHover(s.pointerType)) this.toolHandler?.onHover?.(s, pt);
      this.toolHandler?.onDown?.(s, pt);
    }
  };

  private handleMove = (e: PointerEvent) => {
    const s = this.toSample(e);
    if (this.isTouchSuppressed(s)) return;
    const tracked = this.active.get(s.pointerId);
    if (tracked) {
      tracked.x = s.x;
      tracked.y = s.y;
    }

    if (this.gesture === "pinch" && this.active.size >= 2) {
      this.queuePinch();
      return;
    }

    if (this.gesture === "pan" && tracked) {
      this.viewport.pan(s.x - this.panLastX, s.y - this.panLastY);
      this.panLastX = s.x;
      this.panLastY = s.y;
      return;
    }

    if (this.gesture === "draw") {
      // Only the contact that started the drag drives it.
      if (this.drawPointerId !== null && s.pointerId !== this.drawPointerId)
        return;
      this.lastDrawSample = s;
      this.toolHandler?.onMove?.(s, this.project(s));
      return;
    }

    if (!tracked) {
      this.toolHandler?.onHover?.(s, this.project(s));
    }
  };

  /** Apply the pinch once per frame, from both fingers' latest positions. */
  private queuePinch() {
    if (this.pinchQueued) return;
    this.pinchQueued = true;
    requestAnimationFrame(() => {
      this.pinchQueued = false;
      if (this.gesture !== "pinch" || !this.pinch) return;
      const [a, b] = [...this.active.values()];
      if (!a || !b) return;
      const up = updatePinch(this.pinch, a, b);
      this.pinch = up.next;
      // Pan BEFORE zoom, and pivot the zoom on the new midpoint: that puts the
      // content that was between the fingers back between the fingers. Zooming
      // first would pin it to the new midpoint and then shift it again by the
      // pan.
      if (up.panDx !== 0 || up.panDy !== 0)
        this.viewport.pan(up.panDx, up.panDy);
      if (up.zoom !== null)
        this.viewport.zoomAt(
          { x: up.pivotX, y: up.pivotY },
          up.zoom / this.viewport.zoom,
        );
    });
  }

  private handleUp = (e: PointerEvent) => {
    const s = this.toSample(e);
    const wasActive = this.active.has(s.pointerId);
    this.active.delete(s.pointerId);
    try {
      this.el.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }

    if (s.pointerType === "pen") {
      // pen up: re-enable touch only when no other pen pointers remain
      const stillPen = [...this.active.values()].some((p) => p.type === "pen");
      this.penActive = stillPen;
    }

    // A finger lifting while the pen is down must not reach the tool. Its
    // moves are already suppressed, but this release used to fall straight
    // into the `gesture === "draw"` branch below and end the PEN's stroke at
    // the FINGER's coordinates — a resting hand terminating a pen line at the
    // heel of your palm. Predates #40 (the finger got there as a pan), but it
    // is the same palm, and it is the half `isTouchSuppressed` never covered.
    if (this.isTouchSuppressed(s)) return;

    if (!wasActive) return;

    if (this.gesture === "pinch" && this.active.size < 2) {
      this.pinch = null;
      this.gesture = this.active.size === 1 ? "pan" : "none";
      const remaining = [...this.active.values()][0];
      if (remaining) {
        this.panLastX = remaining.x;
        this.panLastY = remaining.y;
      }
      return;
    }

    if (this.gesture === "draw") {
      // A second finger lifting must not end a drag it never started.
      if (this.drawPointerId !== null && s.pointerId !== this.drawPointerId)
        return;
      this.toolHandler?.onUp?.(s, this.project(s));
      this.drawPointerId = null;
      this.lastDrawSample = null;
      this.gesture = this.active.size === 0 ? "none" : this.gesture;
    } else if (this.active.size === 0) {
      this.gesture = "none";
    }
  };

  private handleCancel = (e: PointerEvent) => {
    this.active.delete(e.pointerId);
    if (this.gesture === "draw" && e.pointerId === this.drawPointerId) {
      const last = this.lastDrawSample;
      this.drawPointerId = null;
      this.lastDrawSample = null;
      // The browser took the pointer away — an edge swipe, a notification, a
      // palm rejection, focus loss — which says nothing about what the user
      // meant (#1112). A deliberate placement (the same gestures that survive a
      // second contact) was already where they put it, so it commits at the
      // last position the tool saw. A stroke being sketched is incomplete, and
      // a half-drawn mark is worse than none, so that one is still abandoned.
      if (last && this.toolHandler?.gestureSurvivesSecondContact?.() === true)
        this.toolHandler.onUp?.(last, this.project(last));
      else this.toolHandler?.onCancel?.();
    } else if (this.gesture === "draw") {
      // Another contact was cancelled, not the one driving — leave the drag.
      return;
    }
    this.gesture = this.active.size === 0 ? "none" : this.gesture;
    if (e.pointerType === "pen") {
      this.penActive = [...this.active.values()].some((p) => p.type === "pen");
    }
  };

  private handleWheel = (e: WheelEvent) => {
    e.preventDefault();
    const rect = this.el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const factor = wheelZoomFactor(e.deltaY, e.deltaMode, rect.height);
    this.viewport.zoomAt({ x, y }, factor);
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.code === "Space" && !this.spaceDown && !isTextTarget(e.target)) {
      this.spaceDown = true;
      this.el.style.cursor = "grab";
    }
  };

  private handleKeyUp = (e: KeyboardEvent) => {
    if (e.code === "Space") {
      this.spaceDown = false;
      this.el.style.cursor = "";
    }
  };
}
